/** Comparing two finished runs of one program (`/runs/compare`): the derivations, pure over what `soloDebrief` already serves for each run. */
import type { SoloDebrief } from "@bsim/contracts";
import { finalRank, headlineValue, type FinalRank } from "../solo/finale";

export interface YearScore { readonly yearNo: number; readonly kind: "played" | "lookThrough"; readonly rate: number }
export interface RunScore {
  readonly years: readonly YearScore[];
  /** The score at the end of the last year, as a decimal against the run's own twin (the same series the finale draws). */
  readonly finalRate: number;
  readonly finalUsdPerShare: number;
  readonly rank: FinalRank | null;
}

/** One run's score at each year-end (played years, then the look-through years), its final score in dollars a share and its rank; null when the player's bank is not in the debrief. */
export function runScore(debrief: SoloDebrief, teamId: string, tiedWithTeamIds: readonly string[] = []): RunScore | null {
  const seat = debrief.replay.seats.find((s) => s.teamId === teamId);
  if (seat === undefined) return null;
  const years = seat.valueCreatedRateByYear.map((rate, i) => ({ yearNo: debrief.replay.years[i]?.yearNo ?? i + 1, kind: debrief.replay.years[i]?.kind ?? "played", rate }));
  return { years, finalRate: years.at(-1)?.rate ?? 0, finalUsdPerShare: headlineValue(debrief).usdPerShare, rank: finalRank(debrief, seat.seatId, tiedWithTeamIds) };
}

/** Two runs compare when they are different runs of the same program: the same bank and scenario, each finished. */
export function comparable(runs: readonly { readonly runId: string; readonly programId: string; readonly phase: string }[], a: string, b: string): boolean {
  const x = runs.find((r) => r.runId === a);
  const y = runs.find((r) => r.runId === b);
  return a !== b && x !== undefined && y !== undefined && x.programId === y.programId && x.phase === "completed" && y.phase === "completed";
}

/** Finished runs grouped by program, each group with two or more (the only ones with something to compare). */
export function comparableGroups<T extends { readonly programId: string; readonly phase: string }>(runs: readonly T[]): readonly (readonly T[])[] {
  const finished = runs.filter((r) => r.phase === "completed");
  return [...new Set(finished.map((r) => r.programId))].map((p) => finished.filter((r) => r.programId === p)).filter((g) => g.length >= 2);
}
